import { useEffect, useRef } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Box, Link as B2BLink, paletteColors, Stack, Typography } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { getEnvRequired } from '../../shared/utilities/env';
import { HttpError } from '../../resources/shared/api/client';
import { useConnectCalendarMutation } from '../../resources/appointment/mutations';
import { OAUTH_STATE_KEY } from '../../resources/appointment/operations';
import { DEFAULT_VIEW_ROUTE } from '../../shared/routing/RequestManagerRoutes';
import { Loader } from '../../design-system/Loader';

const APP_BASENAME = getEnvRequired('RM_APP_BASENAME');
const AGENDA_CONFIG_ROUTE = '/appointment';

function buildRedirectUri(): string {
    return `${window.location.origin}${APP_BASENAME}/calendar/callback`;
}

export const GoogleOAuthCallbackPage = (): JSX.Element => {
    const location = useLocation();
    const navigate = useNavigate();
    const mutation = useConnectCalendarMutation();
    const hasFired = useRef(false);
    const { formatMessage } = useIntl();

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const error = params.get('error');
    const stateParam = params.get('state');

    // Lecture et purge du state stocké, en une seule fois au premier rendu.
    // Le sentinel `undefined` empêche de relire sessionStorage sur les re-renders :
    // après le premier passage, storedStateRef.current vaut la valeur lue (ou null),
    // jamais undefined, donc le bloc if est ignoré pour tous les rendus suivants.
    // Usage unique garanti : removeItem est appelé ici même en cas d'échec de validation.
    const storedStateRef = useRef<string | null | undefined>(undefined);
    if (storedStateRef.current === undefined) {
        storedStateRef.current = sessionStorage.getItem(OAUTH_STATE_KEY);
        sessionStorage.removeItem(OAUTH_STATE_KEY);
    }
    // Valide si et seulement si Google a renvoyé exactement le même state que celui
    // qu'on avait généré. Un state absent ou différent indique une tentative de CSRF.
    const isStateValid = stateParam !== null && stateParam === storedStateRef.current;

    useEffect(() => {
        if (code && isStateValid && !hasFired.current) {
            hasFired.current = true;
            mutation.mutate(
                { provider: 'google', authorizationCode: code, redirectUri: buildRedirectUri() },
                { onSuccess: () => navigate(AGENDA_CONFIG_ROUTE, { replace: true }) },
            );
        }
        // hasFired and mutation.mutate are stable refs — intentional omission
        // eslint-disable-next-line react-hooks/exhaustive-deps -- hasFired and mutation.mutate are stable refs, intentional omission
    }, []);

    const backToHomeLink = (
        <B2BLink
            component={RouterLink}
            to={DEFAULT_VIEW_ROUTE}
            data-spec-id="calendar-callback-back-home"
        >
            {formatMessage({ id: 'calendar.callback.back_to_home' })}
        </B2BLink>
    );

    const content = (() => {
        if (error === 'access_denied') {
            return (
                <Stack gap={2} alignItems="center">
                    <Typography data-spec-id="calendar-connect-refused-message">
                        {formatMessage({ id: 'calendar.connect.refused' })}
                    </Typography>
                    {backToHomeLink}
                </Stack>
            );
        }

        if (code && !isStateValid) {
            return (
                <Stack gap={2} alignItems="center">
                    <Typography data-spec-id="calendar-connect-security-error">
                        {formatMessage({ id: 'calendar.connect.security_error' })}
                    </Typography>
                    {backToHomeLink}
                </Stack>
            );
        }

        if (mutation.isPending || mutation.isSuccess) {
            return <Loader />;
        }

        if (mutation.isError) {
            const httpStatus =
                mutation.error instanceof HttpError ? mutation.error.status : undefined;

            if (httpStatus === 409) {
                return (
                    <Stack gap={2} alignItems="center">
                        <Typography data-spec-id="calendar-already-connected-message">
                            {formatMessage({ id: 'calendar.connect.already_connected' })}
                        </Typography>
                        <Typography data-spec-id="calendar-disconnect-prompt">
                            {formatMessage({ id: 'calendar.connect.disconnect_first' })}
                        </Typography>
                        {backToHomeLink}
                    </Stack>
                );
            }

            return (
                <Stack gap={2} alignItems="center">
                    <Typography data-spec-id="calendar-connect-error-message">
                        {formatMessage({ id: 'calendar.connect.error_message' })}
                    </Typography>
                    {backToHomeLink}
                </Stack>
            );
        }

        return (
            <Stack gap={2} alignItems="center">
                <Typography>
                    {formatMessage({ id: 'calendar.connect.invalid_callback' })}
                </Typography>
                {backToHomeLink}
            </Stack>
        );
    })();

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                backgroundColor: paletteColors.neutral[0],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 3,
                textAlign: 'center',
            }}
        >
            {content}
        </Box>
    );
};
